import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import {
  type DeviceMode,
  deviceModeAtom,
  selectedDeviceStateAtom,
  showClientIdentifierSelector,
} from '../../../models/atoms/device_atoms';
import { useState, useEffect, useRef, type ChangeEvent } from 'react';
import { company_controller } from '../../../controllers/company_controller';
import { device_controller } from '../../../controllers/device_controller';
import { useTranslation } from '../../../contexts/TranslationContext'; // Import the useTranslation hook
import { accountCompanyInfoAtom } from '../../../models/atoms/account_atoms';
import {
  MenuItem,
  Select,
  SelectChangeEvent,
  Paper,
  Stack,
  useTheme,
  InputLabel,
  FormControl,
} from '@mui/material';
import { canManageDevicesSelector } from '../../../models/selectors/permission_selectors';
import { deviceSessionsSelector } from '../../../models/selectors/device_selectors';
import { ArchivedSession } from '../../../models/interfaces/device_interfaces';
import { MHMTypography } from '../MHM/MHMTypography';
import { Close, PushPin } from '@mui/icons-material';
import { MHMTextField } from '../MHM/MHMTextField';
import { MHMIconButton } from '../MHM/MHMIconButton';
import { DeviceInfoSection } from './DeviceInfoSection';
import { DeviceInfo } from './DeviceInfo';
import { CheckPermission } from '../CheckPermission';
import { DeviceReadings } from './DeviceReadings';
import { DeviceDataVisualisation } from './DeviceDataVisualisation';
import { DeviceTransmissions } from './DeviceTransmissions';
import { DeviceInstallationInfo } from './DeviceInstallationInfo';
import { unix_timestamp_to_date_time } from '../../../utils/helper_functions';

function getSessionValue(session: ArchivedSession): string {
  return typeof session === 'string' ? session : session.session_id;
}

export const DeviceInfoPanel = (
  _: React.DetailedHTMLProps<
    React.HTMLAttributes<HTMLDivElement>,
    HTMLDivElement
  >,
) => {
  const { t, language } = useTranslation(); // Use the translation hook
  const selectedDevice = useRecoilValue(selectedDeviceStateAtom);
  const setSelectedDevice = useSetRecoilState(selectedDeviceStateAtom);
  const selectedSessions = useRecoilValue(deviceSessionsSelector);
  const accountState = useRecoilValue(accountCompanyInfoAtom);
  const ref = useRef<HTMLDivElement>(null);
  const canManageDevices = useRecoilValue(canManageDevicesSelector);
  const showClientIdentifier = useRecoilValue(showClientIdentifierSelector);
  const [clientIdentifier, setClientIdentifier] = useState(
    selectedDevice?.client_identifier,
  );
  const [clientIdentifierChanged, setClientIdentifierChanged] = useState(false);
  const {
    assets: { logoURL },
  } = useTheme();
  const [mode, setMode] = useRecoilState(deviceModeAtom);

  const sessionID = selectedDevice?.session_id;

  const canEditSession = canManageDevices && sessionID === null;

  function handleClientIdentifierChange(event: ChangeEvent<HTMLInputElement>) {
    setClientIdentifier(event.target.value);
    setClientIdentifierChanged(true);
  }

  async function handleSaveClientIdentifier() {
    if (!selectedDevice) {
      return;
    }

    if (typeof clientIdentifier !== 'string' || !clientIdentifierChanged) {
      return;
    }

    await device_controller.changeClientIdentifier(
      selectedDevice.device_id,
      clientIdentifier,
    );
    setClientIdentifierChanged(false);
  }

  function handleSessionChange(e: SelectChangeEvent<string>) {
    if (!selectedDevice) {
      return;
    }

    let newMode: DeviceMode = 'live';
    let session = '';
    if (e.target.value !== 'Current Session') {
      newMode = 'archived';
      session = e.target.value;
    }
    if (newMode !== mode) {
      setMode(newMode);
      device_controller.get_device_list();
    }
    device_controller.select_device(selectedDevice?.device_id, session);
  }

  function formatSessionLabel(session: ArchivedSession): string {
    if (session.start_date && session.end_date) {
      const startDate = unix_timestamp_to_date_time(
        session.start_date,
        false,
        true,
        false,
        language,
      );
      const endDate = unix_timestamp_to_date_time(
        session.end_date,
        false,
        true,
        false,
        language,
      );
      return `${t('Session', { sessionId: session.session_id })} (${startDate} - ${endDate})`;
    }

    return t('Session', { sessionId: session.session_id });
  }

  useEffect(() => {
    company_controller.get_low_power_settings();
    company_controller.get_weathermode_settings();
    company_controller.get_roc_settings();
  }, []);

  useEffect(() => {
    if (!selectedDevice) {
      return;
    }

    if (ref.current !== null) {
      ref.current.scrollTo(0, 0);
    }

    setClientIdentifier(selectedDevice.client_identifier);
    setClientIdentifierChanged(false);
  }, [selectedDevice]);

  if (selectedDevice === null) {
    return (
      <Stack
        justifyContent="center"
        alignItems="center"
        height="100%"
        width="100%"
      >
        <img src={logoURL} alt="logo" style={{ width: '80%' }} />
      </Stack>
    );
  }

  return (
    <Paper
      data-testid="Device Info Component"
      ref={ref}
      component="article"
      sx={{ height: '100%', overflowY: 'auto' }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          position: 'sticky',
          top: 0,
          bgcolor: 'primary.main',
          p: '0.5rem 0.5rem 0.5rem 1rem',
          zIndex: 2,
        }}
      >
        <MHMTypography variant="h1" color="highlight">
          {t('Device ID:')} {selectedDevice.device_id}
        </MHMTypography>
        {selectedDevice.pinned ? (
          <PushPin fontSize="large" color="highlight" sx={{ mr: 'auto' }} />
        ) : null}
        <MHMIconButton onClick={() => setSelectedDevice(null)}>
          <Close fontSize="large" color="highlight" />
        </MHMIconButton>
      </Stack>

      <Stack style={{ padding: '1rem' }} gap={2}>
        {showClientIdentifier ? (
          <div>
            <DeviceInfoSection.Entry
              label={`${t('Client Identifier')}:`}
              justifyContent="space-between"
              flexWrap="wrap"
            >
              <CheckPermission
                hasPermission={canManageDevices}
                sx={{ minWidth: '50%', flex: 1 }}
              >
                <MHMTextField
                  value={clientIdentifier ?? ''}
                  fullWidth
                  disabled={!canEditSession}
                  onChange={handleClientIdentifierChange}
                />
              </CheckPermission>
            </DeviceInfoSection.Entry>
            {clientIdentifierChanged && canEditSession ? (
              <DeviceInfoSection.Button
                onClick={handleSaveClientIdentifier}
                sx={{ mt: 0 }}
              >
                {t('Save')}
              </DeviceInfoSection.Button>
            ) : null}
          </div>
        ) : null}

        {selectedSessions && selectedSessions.length > 0 && (
          <FormControl>
            <InputLabel id="session-select-label">
              {t('Select session')}
            </InputLabel>
            <Select
              value={selectedDevice?.session_id ?? 'Current Session'}
              onChange={handleSessionChange}
              label={t('Select session')}
              labelId="session-select-label"
              size="small"
            >
              {selectedDevice.has_live_session ? (
                <MenuItem value={'Current Session'} disabled={!sessionID}>
                  {t('Current Session')}
                </MenuItem>
              ) : null}

              {selectedSessions.map(s => (
                <MenuItem key={getSessionValue(s)} value={getSessionValue(s)}>
                  {formatSessionLabel(s)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <DeviceInfo canEditSession={canEditSession} />

        <DeviceReadings canEditSession={canEditSession} />

        <DeviceDataVisualisation session={sessionID} />

        <DeviceTransmissions />

        <DeviceInstallationInfo canEditSession={canEditSession} />

        {(accountState?.name === 'Demo' ||
          accountState?.name === 'Demo US' ||
          accountState?.name === 'Oldcastle') && (
          <DeviceInfoSection>
            <DeviceInfoSection.Button
              size="large"
              onClick={() =>
                device_controller.hide_device(
                  selectedDevice.device_id,
                  sessionID ?? undefined,
                )
              }
            >
              {t('Hide Device')}
            </DeviceInfoSection.Button>
          </DeviceInfoSection>
        )}
      </Stack>
    </Paper>
  );
};
